import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Close as CloseIcon } from "@mui/icons-material";
import { BILightGrey } from "../../../assets/helpers";
import { Getrequestcall } from "../../../request/Getrequest";
import { Postrequestcall } from "../../../request/Postrequest";
import {
  GET_RECENTLY_INTERACT_USERS,
  SAVE_RECENTLY_INTERACT_USERS,
  SEARCH_USERS_CONTACT,
  SEND_MONTAGE
} from "../../../constant/Apipath";
import "../Montage.css";
import defaultPicture from "../../../assets/images/profile_picture_placeholder.png";

export default function ShareMontageModal({ 
  open, 
  onClose, 
  selectedMontage, 
  loginData, 
  userID, 
  dbValues 
}) {
  const [toSendWith, setToSendWith] = useState([]);
  const [recentInteractUsers, setRecentInteractUsers] = useState([]);
  const [emailSearchTerm, setEmailSearchTerm] = useState("");
  const [userList, setUserList] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const defaultLinkRef = useRef(null);
  const montId = selectedMontage?.id;

  useEffect(() => {
    if (open) {
      Promise.all([fetchRecentInteractUsers(), getUserList()]);
    }
  }, [open]);

  const fetchRecentInteractUsers = async () => {
    try {
      const url = `${GET_RECENTLY_INTERACT_USERS}/${userID}`;
      const res = await Getrequestcall(url, loginData?.accessToken);
      const updatedData = (res?.data?.data ?? []).map((user) => ({
        ...user,
        isSelected: false
      }));
      setRecentInteractUsers(updatedData);
    } catch (error) {
      console.error("fetchRecentInteractUsers-> Error fetching recent interact users: ", error);
    }
  };

  const getUserList = async () => {
    try {
      const apiUrl = `${SEARCH_USERS_CONTACT}/${userID}`;
      const response = await Getrequestcall(apiUrl, loginData?.accessToken);
      setUserList(response?.data?.data ?? []);
    } catch (error) {
      console.error("getUserList-> Error fetching user list: ", error);
    }
  };

  const handleShareMontage = async () => {
    setLoading(true);
    const shareData = toSendWith.map((user) => ({
      EmailId: user.contactEmail || user,
      FullName: "",
      MontageUrl: `https://bimiscwebapi-montage-viewer.azurewebsites.net/?db=${dbValues}/&email=${
        user.contactEmail || user
      }/&montageId=${montId}`
    }));

    if (shareData.length === 0) return;

    try {
      const responses = await Promise.all(
        shareData.map((item) => Postrequestcall(SEND_MONTAGE, item, loginData?.accessToken))
      );

      const selectedUsers = shareData.filter((item) => item.EmailId).map((item) => item.EmailId);
      if (responses.every((res) => res?.data?.message === "OK")) {
        await saveRecentInteractions(selectedUsers);
        setToSendWith([]);
        setEmailSearchTerm("");
        onClose();
      }
    } catch (error) {
      console.error("handleShareMontage-> Error sharing montage: ", error);
    } finally {
      setLoading(false);
    }
  };

  const saveRecentInteractions = async (selectedUsers) => {
    const interactionRequests = toSendWith
      .filter((item) => item.contactId)
      .map((item) => ({
        UserId: Number(userID),
        SaveList: [
          {
            Id: 0,
            UserId: Number(userID),
            InteractUserId: item.contactId,
            ModifiedBy: Number(userID),
            fromScreen: "Resume"
          }
        ]
      }));

    await Promise.all(
      interactionRequests.map((request) =>
        Postrequestcall(SAVE_RECENTLY_INTERACT_USERS, request, loginData?.accessToken)
      )
    );

    fetchRecentInteractUsers();
  };

  const handleRecentInteractUserClick = (user) => {
    setRecentInteractUsers((prevState) =>
      prevState.map((recent) =>
        recent.id === user.id ? { ...recent, isSelected: !recent.isSelected } : recent
      )
    );
    if (user.isSelected) {
      setToSendWith((prev) => prev.filter((item) => item.contactId !== user.interactUserId));
    } else {
      setToSendWith((prev) => [
        ...prev,
        {
          contactEmail: user.interactUserEmail,
          contactId: user.interactUserId
        }
      ]);
    }
  };

  // Handle copy link action
  const handleCopyLink = () => {
    navigator.clipboard.writeText(defaultLinkRef.current.value);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleEmailRemove = (removedEmail) => {
    setToSendWith((prevState) => {
      return prevState.filter((email) => email.contactEmail !== removedEmail.contactEmail);
    });
  };

  const handleInputChange = (e) => {
    setEmailSearchTerm(e.target.value.trim());
  };

  const handleClose = () => {
    onClose();
    // Optionally reset state when closing
    // setToSendWith([]);
    // setEmailSearchTerm("");
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="modal-center"
        sx={{
          width: { xs: "100%", sm: "700px" },
          padding: "15px"
        }}>
        <Box className="modal-card" sx={{ height: { lg: "350px", sm: "400px", xs: "400px" } }}>
          <IconButton onClick={handleClose} color="primary">
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Share Montage
          </Typography>
          <Typography sx={{ textAlign: "center" }}>
            Add users or groups to give them access to view the montage
          </Typography>

          <Box sx={{ mx: 2,  mt: 2,  alignItems: "center",  display: "flex" }}   >
            <TextField
              value={`https://bimiscwebapi-montage-viewer.azurewebsites.net/?db=${dbValues}/&email=unknown/&montageId=${montId}`}
              inputRef={defaultLinkRef}
              fullWidth
              variant="outlined"
              size="small"
              sx={{ backgroundColor: BILightGrey }}
            />
            <Button
              sx={{
                ml: 1,
                mr: 2,
                height: "40px",
                display: { xs: "none", sm: "inline-flex" }
              }}
              startIcon={copySuccess ? <CheckIcon sx={{ color: "#fff" }} /> : <ContentCopyIcon />}
              color="primary"
              variant="contained"
              onClick={handleCopyLink}
            />
          </Box>

          <Grid container spacing={2} sx={{ ml: { lg: "center", xs: "20px", sm: "20px" } }}>
            {recentInteractUsers.length > 0 && (
              <div>
                <Typography variant="h6">
                  <b>Recent</b>
                </Typography>
                <Grid container item size={{ xs: 12 }} spacing={1}>
                  {recentInteractUsers.map((user) => (
                    <Grid
                      item
                      size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                      key={user?.userId}
                      sx={{ cursor: "pointer" }}>
                      <div onClick={() => handleRecentInteractUserClick(user)}>
                        <div
                          style={{
                            display: "flex",
                            marginTop: "10px",
                            gap: "10px"
                          }}>
                          <div>
                            <Avatar
                              sx={{ width: 40, height: 40 }}
                              alt={user?.interactUserName}
                              src={user?.interactUserImageUrl}
                            />
                          </div>
                          <div sx={{ ml: 1 }}>
                            <span
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}>
                              <b>
                                {user?.interactUserName.length > 20
                                  ? user?.interactUserName.substring(0, 20) + "..."
                                  : user?.interactUserName}
                              </b>
                              <br />
                              {user?.interactUserTradeName.length > 20
                                ? user?.interactUserTradeName.substring(0, 20) + "..."
                                : user?.interactUserTradeName}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </Grid>

          <Box sx={{ mx: 2,  mt: 2 }} >
            <Autocomplete
              id="sendWith-autocomplete"
              inputValue={emailSearchTerm}
              multiple
              freeSolo
              filterSelectedOptions
              options={emailSearchTerm.length >= 1 ? userList : []}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                return option.contactEmail || option.contactName || "";
              }}
              renderOption={(props, option) => (
                <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                  {option.contactPictureUrl !== "" && (
                    <img
                      style={{
                        borderRadius: "50%",
                        marginRight: "10px"
                      }}
                      loading="lazy"
                      width="50px"
                      height="50px"
                      src={option.contactPictureUrl ?? defaultPicture}
                      alt=""
                    />
                  )}
                  {option?.contactName} <br />
                  {option?.contactEmail}
                </Box>
              )}
              value={toSendWith}
              onChange={(event, newValue, reason) => {
                if (reason === "removeOption") {
                  const removedItem = toSendWith.find((item) => !newValue.includes(item));
                  if (removedItem) {
                    handleEmailRemove(removedItem);
                  }
                } else {
                  setToSendWith(newValue);
                }
                setEmailSearchTerm("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  onChange={handleInputChange}
                  fullWidth
                  label="Add Contacts or emails"
                  value={emailSearchTerm}
                />
              )}
              style={{ flex: 1, marginTop: "10px" }}
            />

            <Button
              onClick={handleShareMontage}
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              sx={{ marginTop: "10px" }}
              disabled={loading || toSendWith.length === 0}
              startIcon={loading && <CircularProgress size={24} />}>
              {loading ? "Sharing..." : "Share Montage"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
